import React, { useState, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, FlatList, ActivityIndicator, Linking,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../contexts/LanguageContext';
import { requestReport, getMyReports, getAllReports } from '../../services/reportService';
import { Report, ReportType } from '../../types';
import CrossPlatformAlert from '../../components/CrossPlatformAlert';
import DateTimePickerInput from '../../components/DateTimePickerInput';

const STATUS_COLORS: Record<string, string> = {
  pending: '#F59E0B',
  done: '#10B981',
  error: '#EF4444',
};

function parseDateStr(str: string): Date | null {
  const parts = str.trim().split('.');
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts.map(Number);
  if (!dd || !mm || !yyyy || dd < 1 || dd > 31 || mm < 1 || mm > 12 || yyyy < 1900) return null;
  const d = new Date(Date.UTC(yyyy, mm - 1, dd));
  if (d.getUTCFullYear() !== yyyy || d.getUTCMonth() !== mm - 1 || d.getUTCDate() !== dd) return null;
  return d;
}

interface DateRange {
  from: string;
  to: string;
}

export default function ReportsScreen() {
  const { appUser } = useAuth();
  const { t } = useLanguage();

  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState<ReportType | null>(null);

  const [usageDateRange, setUsageDateRange] = useState<DateRange>({ from: '', to: '' });
  const [realUsageDateRange, setRealUsageDateRange] = useState<DateRange>({ from: '', to: '' });

  const role = appUser?.role ?? 'guest';
  const canView = role === 'admin' || role === 'manager';
  const isAdmin = role === 'admin';

  const maxDate = (() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  })();

  const load = useCallback(async () => {
    if (!appUser) return;
    setLoading(true);
    try {
      const data = isAdmin ? await getAllReports() : await getMyReports(appUser.uid);
      setReports(data);
    } finally {
      setLoading(false);
    }
  }, [appUser, isAdmin]);

  useFocusEffect(useCallback(() => { load(); }, [load]));

  const parseDateRange = (range: DateRange): { timeFrom?: Date; timeTo?: Date } | null => {
    const from = range.from.trim();
    const to = range.to.trim();
    const timeFrom = from ? parseDateStr(from) : undefined;
    const timeTo = to ? parseDateStr(to) : undefined;
    if (from && !timeFrom) return null;
    if (to && !timeTo) return null;
    if (timeTo) {
      const endOfDay = new Date(Date.UTC(timeTo.getUTCFullYear(), timeTo.getUTCMonth(), timeTo.getUTCDate(), 23, 59, 59, 999));
      return { timeFrom: timeFrom ?? undefined, timeTo: endOfDay };
    }
    return { timeFrom: timeFrom ?? undefined, timeTo: undefined };
  };

  const handleRequest = async (type: ReportType) => {
    if (!appUser) return;

    let timeFrom: Date | undefined;
    let timeTo: Date | undefined;

    if (type === 'usage_records' || type === 'real_usage') {
      const range = type === 'usage_records' ? usageDateRange : realUsageDateRange;
      const parsed = parseDateRange(range);
      if (parsed === null) {
        CrossPlatformAlert.alert(t('common.validation'), t('reports.dateRangeError'));
        return;
      }
      timeFrom = parsed.timeFrom;
      timeTo = parsed.timeTo;
    }

    setRequesting(type);
    try {
      await requestReport(type, appUser.uid, appUser.email, timeFrom, timeTo);
      CrossPlatformAlert.alert('', t('reports.requestSuccess'));
      load();
    } catch {
      CrossPlatformAlert.alert(t('common.error'), t('reports.requestError'));
    } finally {
      setRequesting(null);
    }
  };

  const statusLabel = (status: string) => {
    if (status === 'pending') return t('reports.statusPending');
    if (status === 'done') return t('reports.statusDone');
    return t('reports.statusError');
  };

  const typeLabel = (type: ReportType) => {
    if (type === 'current_quantity') return t('reports.currentQuantityTitle');
    if (type === 'real_usage') return t('reports.realUsageTitle');
    if (type === 'below_limit') return t('reports.belowLimitTitle');
    return t('reports.usageRecordsTitle');
  };

  if (!canView) {
    return <View style={styles.center}><Text style={styles.noAccess}>{t('common.noAccess')}</Text></View>;
  }

  const renderDateRange = (range: DateRange, setRange: React.Dispatch<React.SetStateAction<DateRange>>) => (
    <View style={styles.dateRow}>
      <View style={styles.dateField}>
        <DateTimePickerInput
          mode="date"
          label={t('reports.dateFromLabel')}
          value={range.from}
          onChange={v => setRange(r => ({ ...r, from: v }))}
        />
      </View>
      <View style={styles.dateSeparator} />
      <View style={styles.dateField}>
        <DateTimePickerInput
          mode="date"
          label={t('reports.dateToLabel')}
          value={range.to}
          onChange={v => setRange(r => ({ ...r, to: v }))}
          maxDate={maxDate}
        />
      </View>
    </View>
  );

  const formatDateRange = (item: Report) => {
    if (!item.timeFrom && !item.timeTo) return null;
    const fmt = (d: Date) => d.toLocaleDateString();
    if (item.timeFrom && item.timeTo) return `${fmt(item.timeFrom)} – ${fmt(item.timeTo)}`;
    if (item.timeFrom) return `${t('reports.dateFromLabel')}: ${fmt(item.timeFrom)}`;
    return `${t('reports.dateToLabel')}: ${fmt(item.timeTo!)}`;
  };

  const listHeader = (
    <View>
      <View style={styles.header}>
        <Text style={styles.title}>{t('reports.title')}</Text>
      </View>

      <Text style={styles.emailNotice}>{t('reports.emailNotice')}</Text>

      {/* Report type cards */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>{t('reports.currentQuantityTitle')}</Text>
        <Text style={styles.cardDescription}>{t('reports.currentQuantityDescription')}</Text>
        <TouchableOpacity
          style={[styles.requestBtn, requesting === 'current_quantity' && styles.requestBtnDisabled]}
          onPress={() => handleRequest('current_quantity')}
          disabled={requesting !== null}
        >
          <Text style={styles.requestBtnText}>
            {requesting === 'current_quantity' ? t('reports.requesting') : t('reports.requestButton')}
          </Text>
        </TouchableOpacity>
      </View>

      <View style={styles.card}>
        <Text style={styles.cardTitle}>{t('reports.usageRecordsTitle')}</Text>
        <Text style={styles.cardDescription}>{t('reports.usageRecordsDescription')}</Text>
        {renderDateRange(usageDateRange, setUsageDateRange)}
        <TouchableOpacity
          style={[styles.requestBtn, requesting === 'usage_records' && styles.requestBtnDisabled]}
          onPress={() => handleRequest('usage_records')}
          disabled={requesting !== null}
        >
          <Text style={styles.requestBtnText}>
            {requesting === 'usage_records' ? t('reports.requesting') : t('reports.requestButton')}
          </Text>
        </TouchableOpacity>
      </View>

      <View style={styles.card}>
        <Text style={styles.cardTitle}>{t('reports.realUsageTitle')}</Text>
        <Text style={styles.cardDescription}>{t('reports.realUsageDescription')}</Text>
        {renderDateRange(realUsageDateRange, setRealUsageDateRange)}
        <TouchableOpacity
          style={[styles.requestBtn, requesting === 'real_usage' && styles.requestBtnDisabled]}
          onPress={() => handleRequest('real_usage')}
          disabled={requesting !== null}
        >
          <Text style={styles.requestBtnText}>
            {requesting === 'real_usage' ? t('reports.requesting') : t('reports.requestButton')}
          </Text>
        </TouchableOpacity>
      </View>

      <View style={styles.card}>
        <Text style={styles.cardTitle}>{t('reports.belowLimitTitle')}</Text>
        <Text style={styles.cardDescription}>{t('reports.belowLimitDescription')}</Text>
        <TouchableOpacity
          style={[styles.requestBtn, requesting === 'below_limit' && styles.requestBtnDisabled]}
          onPress={() => handleRequest('below_limit')}
          disabled={requesting !== null}
        >
          <Text style={styles.requestBtnText}>
            {requesting === 'below_limit' ? t('reports.requesting') : t('reports.requestButton')}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Recent reports list heading */}
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>{t('reports.recentReports')}</Text>
        <TouchableOpacity
          style={styles.refreshBtn}
          onPress={load}
          disabled={loading}
        >
          <Text style={styles.refreshBtnText}>{t('reports.refresh')}</Text>
        </TouchableOpacity>
      </View>

      {loading && <ActivityIndicator style={{ marginTop: 20 }} size="large" color="#2563EB" />}
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={styles.innerContent}>
        <FlatList
          data={loading ? [] : reports}
          keyExtractor={item => item.id}
          ListHeaderComponent={listHeader}
          renderItem={({ item }) => {
            const dateRange = formatDateRange(item);
            return (
              <View style={styles.reportRow}>
                <View style={styles.reportInfo}>
                  <Text style={styles.reportType}>{typeLabel(item.type)}</Text>
                  <Text style={styles.reportDate}>
                    {item.createdAt.toLocaleDateString()} {item.createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Text>
                  {isAdmin && (item.requestedByEmail || item.recipients?.length) ? (
                    <Text style={styles.reportGeneratedBy}>
                      {t('reports.generatedBy')}: {item.requestedByEmail || t('reports.systemLabel')}
                      {item.recipients && item.recipients.length > 0
                        ? ` · ${t('reports.recipientsLabel')}: ${item.recipients.join(', ')}`
                        : null}
                    </Text>
                  ) : isAdmin && !item.requestedByEmail && !item.recipients?.length ? (
                    <Text style={styles.reportGeneratedBy}>
                      {t('reports.generatedBy')}: {t('reports.systemLabel')}
                    </Text>
                  ) : null}
                  {dateRange ? (
                    <Text style={styles.reportDateRange}>{dateRange}</Text>
                  ) : null}
                  {item.errorMessage ? (
                    <Text style={styles.reportError}>{item.errorMessage}</Text>
                  ) : null}
                </View>
                <View style={styles.reportActions}>
                  {item.status === 'done' && item.downloadUrl ? (
                    <TouchableOpacity
                      style={styles.downloadBtn}
                      onPress={() => Linking.openURL(item.downloadUrl!)}
                    >
                      <Text style={styles.downloadBtnText}>{t('reports.downloadButton')}</Text>
                    </TouchableOpacity>
                  ) : null}
                  <View style={[styles.statusBadge, { backgroundColor: STATUS_COLORS[item.status] ?? '#94A3B8' }]}>
                    <Text style={styles.statusText}>{statusLabel(item.status)}</Text>
                  </View>
                </View>
              </View>
            );
          }}
          ListEmptyComponent={!loading ? <Text style={styles.empty}>{t('reports.noReports')}</Text> : null}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  innerContent: { flex: 1, maxWidth: 960, width: '100%', alignSelf: 'center' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  noAccess: { fontSize: 18, color: '#6B7280' },
  header: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    padding: 16, borderBottomWidth: 1, borderBottomColor: '#E2E8F0',
  },
  title: { fontSize: 22, fontWeight: 'bold', color: '#1E293B' },
  emailNotice: {
    marginHorizontal: 16, marginTop: 12, marginBottom: 4,
    fontSize: 13, color: '#64748B', fontStyle: 'italic',
  },
  card: {
    backgroundColor: '#FFF', margin: 12, marginBottom: 4, borderRadius: 12, padding: 16,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05, shadowRadius: 4, elevation: 1,
  },
  cardTitle: { fontSize: 17, fontWeight: '700', color: '#1E293B', marginBottom: 4 },
  cardDescription: { fontSize: 13, color: '#64748B', marginBottom: 12 },
  dateRow: { flexDirection: 'row', alignItems: 'flex-end', marginBottom: 12 },
  dateField: { flex: 1 },
  dateSeparator: { width: 12 },
  requestBtn: {
    backgroundColor: '#2563EB', paddingVertical: 10, paddingHorizontal: 16,
    borderRadius: 8, alignSelf: 'flex-start',
  },
  requestBtnDisabled: { backgroundColor: '#93C5FD' },
  requestBtnText: { color: '#FFF', fontWeight: '600', fontSize: 14 },
  sectionHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginHorizontal: 16, marginTop: 20, marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 16, fontWeight: '700', color: '#1E293B',
  },
  refreshBtn: {
    backgroundColor: '#E2E8F0', paddingVertical: 6, paddingHorizontal: 12, borderRadius: 8,
  },
  refreshBtnText: { color: '#2563EB', fontWeight: '600', fontSize: 13 },
  reportRow: {
    backgroundColor: '#FFF', marginHorizontal: 12, marginBottom: 6, borderRadius: 10,
    padding: 14, flexDirection: 'row', alignItems: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04, shadowRadius: 3, elevation: 1,
  },
  reportInfo: { flex: 1 },
  reportActions: { flexDirection: 'column', alignItems: 'flex-end', gap: 6 },
  downloadBtn: {
    backgroundColor: '#10B981', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8,
  },
  downloadBtnText: { color: '#FFF', fontSize: 12, fontWeight: '700' },
  reportType: { fontSize: 14, fontWeight: '600', color: '#1E293B' },
  reportDate: { fontSize: 12, color: '#64748B', marginTop: 2 },
  reportGeneratedBy: { fontSize: 12, color: '#64748B', marginTop: 2, fontStyle: 'italic' },
  reportDateRange: { fontSize: 12, color: '#2563EB', marginTop: 2 },
  reportError: { fontSize: 11, color: '#EF4444', marginTop: 2 },
  statusBadge: {
    paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, marginLeft: 8,
  },
  statusText: { color: '#FFF', fontSize: 12, fontWeight: '700' },
  empty: { textAlign: 'center', color: '#94A3B8', marginTop: 30, fontSize: 15, padding: 16 },
});
